// @ts-strict-ignore
import {reporter} from 'common/report/error-reporting';
import {StorageManager, StorageSetOptions} from 'common/contexts/storage-manager/types';

import {LocalStorageKey} from './constants';

interface UserLocalStorageEntry<T> {
  value: T;
  expiry: number | null;
}

interface UserLocalStorageData<T> {
  [key: string]: UserLocalStorageEntry<T>;
}

interface UserLocalStorageSchema<T> {
  [userId: string]: UserLocalStorageData<T>;
}

// Inconsistent typing of userId throughout the code.
// Will be serialized as a string key either way.
type UserId = string | number;

/**
 * Wrapper around the StorageManager context to keep data keyed by userId in a nested JSON.
 * All data is stored under a single localStorage key, specified by @param namespace
 *
 * Note that we use this for session storage in addition to local storage, depending
 * on the StorageManager context passed to the constructor
 */
export class UserLocalStorageHelper<T> {
  private declare localStorageContext: StorageManager;
  private declare namespace: LocalStorageKey;
  private declare ttlMs: number;

  constructor({namespace, ttlMs, context}: {namespace: LocalStorageKey; ttlMs?: number; context: StorageManager}) {
    this.localStorageContext = context;
    this.namespace = namespace;
    this.ttlMs = ttlMs;
  }

  public setItem({
    userId,
    key,
    value,
    options,
  }: {
    userId: UserId;
    key: ObjKey;
    value: T;
    options?: StorageSetOptions;
  }): Promise<boolean> | boolean {
    const expiry = Number.isInteger(this.ttlMs) ? new Date().getTime() + this.ttlMs : null;

    // Don't override existing user data
    const userData: UserLocalStorageData<T> = {
      ...this.getUserData(userId),
      [key]: {value, expiry},
    };

    return this.localStorageContext.setItem(this.namespace, this.serializeData(userId, userData), options);
  }

  public getItem({key, userId}: {key: ObjKey; userId: UserId}): T | null {
    const userData = this.getUserData(userId);
    const data = userData[key];

    // Clean up if data is expired
    if (data?.expiry && data.expiry < new Date().getTime()) {
      this.removeItem({key, userId});
      return null;
    }

    return data?.value ?? null;
  }

  public removeItem({key, userId}: {key: ObjKey; userId: UserId}): void {
    const userData = this.getUserData(userId);
    delete userData[key];
    this.localStorageContext.setItem(this.namespace, this.serializeData(userId, userData));
  }

  public addStorageEventListener(callback: () => any): void {
    this.localStorageContext.addStorageEventListener(callback);
  }

  public removeStorageEventListener(callback: () => any): void {
    this.localStorageContext.removeStorageEventListener(callback);
  }

  private getUserData(userId: UserId): UserLocalStorageData<T> {
    const storedItem = this.localStorageContext.getItem(this.namespace);
    try {
      const item = JSON.parse(storedItem);
      if (item?.[userId]) {
        return item[userId];
      }
    } catch (err) {
      reporter.error(err);
    }

    return {};
  }

  private serializeData(userId: UserId, data: UserLocalStorageData<T>): string {
    const userData: UserLocalStorageSchema<T> = {[userId]: data};
    return JSON.stringify(userData);
  }
}
